// src/app/lib/datadisplay.defaults.ts
// Global default field sets per Sigma/hunt category, expressed as CCM field names.
// Tools apply mapField() at render time — no tool-specific knowledge here.
// Fields listed in summary order; detail adds further fields to the end of the summary list.

export interface CategoryDisplayFields {
  summary: string[];
  detail: string[];
  identityFields: string[]; // stable-context fields used as auto-groupBy in aggregate mode
}

/**
 * Default field projection sets per category.
 * summary: minimal fields for quick triage
 * detail:  summary fields + deeper forensic/contextual fields
 * identityFields: stable entity identifiers used as GROUP BY when displayMode='aggregate'
 */
export const defaultDisplayFields: Record<string, CategoryDisplayFields> = {
  process_creation: {
    summary: ['parent_process_path', 'process_path', 'process_cmdline'],
    detail: [
      'username',
      'parent_process_path',
      'parent_process_id',
      'parent_process_cmdline',
      'process_name',
      'process_path',
      'process_cmdline',
      'process_id',
    ],
    identityFields: ['hostname'],
  },

  file_event: {
    summary: ['process_path'],
    detail: [
      'hostname',
      'username',
      'process_path',
      'sha256',
      'md5',
    ],
    identityFields: ['hostname', 'target_path'],
  },

  network_connection: {
    summary: ['hostname','dest_ip', 'dest_port'],
    detail: [
      'hostname',
      'username',
      'dest_ip',
      'dest_port',
      'protocol',
      'process_path',
    ],
    identityFields: ['src_ip'],
  },

  registry_event: {
    summary: ['registry_path', 'registry_key_name', 'registry_value_name'],
    detail: [
      'username',
      'registry_path',
      'registry_key_name',
      'registry_value_name',
      'process_path',
      'process_name'
    ],
    identityFields: ['hostname'],
  },

  dns: {
    summary: ['dest_ip', 'dns_query', 'dns_answer'],
    detail: [
      'hostname',
      'dns_query',
      'dns_answer',
      'dest_ip',
      'dest_port',
      'protocol',
    ],
    // hostname (mapped to Computer in Sysmon) resolves cleanly across both
    // Sysmon DNS events (event id 22) and dedicated DNS log sources, where
    // src_ip is unavailable on the Sysmon side.
    identityFields: ['hostname'],
  },

  image_load: {
    summary: ['object_name', 'object_loaded'],
    detail: [
      'username',
      'object_name',
      'object_loaded',
      'process_path',
      'process_cmdline',
      'sha256',
      'md5',
    ],
    identityFields: ['hostname'],
  },

  authentication_events: {
    summary: ['hostname', 'event_type', 'logon_type'],
    detail: [
      'event_type',
      'event_id',
      'hostname', 
      'logon_process',
      'logon_type',
      'src_ip', 
      'dest_ip'
    ],
    identityFields: ['username'],
  },

  pipe_created: {
    summary: ['username', 'pipe_name', 'process_path'],
    detail: [
      'username', 
      'pipe_name', 
      'process_path', 
      'process_cmdline', 
      'process_id'
    ],
    identityFields: ['hostname'],
  },

  security: {
    summary: ['username', 'event_id'],
    detail: [
      'username', 
      'event_id', 
      'message'
    ],
    identityFields: ['hostname'],
  },

  system: {
    summary: ['event_id','message'],
    detail: [
      'hostname', 
      'event_id', 
      'message'
    ],
    identityFields: ['hostname'],
  },

  cloud: {
    summary: ['username', 'user_agent', 'message'],
    detail: [
      'username', 
      'user_agent', 
      'region', 
      'account_id', 
      'event_type',
      'message'
    ],
    identityFields: ['event_name'],
  },

  firewall: {
    summary: ['dest_port', 'protocol'],
    detail: [
      'hostname',
      'bytes_in',
      'bytes_out',
      'src_port',
      'dest_port',
      'protocol',
      'process_path'
    ],
    identityFields: ['src_ip', 'dest_ip'],
  },

  proxy: {
    summary: ['domain', 'url', 'http_method', 'http_status'],
    detail: [
      'hostname',
      'domain',
      'url',
      'uri',
      'referrer',
      'request_method',
      'post_body',
      'status_code',
      'user_agent'
    ],
    identityFields: ['src_ip'],
  },

  // Note: Sigma uses capital R per the HuntCategoryEnum definition
  Rdp_event: {
    summary: ['username'],
    detail: [
      'username', 
      'dest_ip'
    ],
    identityFields: ['hostname', 'src_ip'],
  },

  application: {
    summary: ['hostname', 'username', 'event_id'],
    detail: [
      'hostname', 
      'username', 
      'event_id', 
      'message'
    ],
    identityFields: ['hostname'],
  },

  webserver: {
    summary: ['hostname', 'url', 'http_method', 'http_status'],
    detail: [
      'hostname', 
      'url', 
      'domain',
      'request_method', 
      'referrer',
      'status_code',
      'user_agent'
    ],
    identityFields: ['src_ip'],
  },
};
